if (!customElements.get('tabbed-product-collections')) {
  class TabbedProductCollections extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-product-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-product-panel]'));
      this.scrollIndicator = this.querySelector('.tabbed-product-collections__scroll-indicator');
      this.scrollThumb = this.querySelector('.tabbed-product-collections__scroll-thumb');
      this.initialiseScrollIndicator();

      if (this.tabs.length && this.panels.length) {
        this.tabListeners = new Map();
        this.tabs.forEach((tab) => {
          const onClick = () => this.activateTab(tab.dataset.productTab);
          const onKeydown = (event) => this.handleKeydown(event);

          tab.addEventListener('click', onClick);
          tab.addEventListener('keydown', onKeydown);
          this.tabListeners.set(tab, { onClick, onKeydown });
        });

        this.onBlockSelect = (event) => this.handleBlockSelect(event);
        document.addEventListener('shopify:block:select', this.onBlockSelect);

        this.classList.add('is-ready');
        this.activateTab(this.tabs[0].dataset.productTab);
      }
    }

    disconnectedCallback() {
      this.tabListeners?.forEach(({ onClick, onKeydown }, tab) => {
        tab.removeEventListener('click', onClick);
        tab.removeEventListener('keydown', onKeydown);
      });

      if (this.onBlockSelect) {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      this.scrollController?.abort();
      this.gridScrollController?.abort();
      this.scrollObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    activateTab(tabId, moveFocus = false) {
      const activeTab = this.tabs.find((tab) => tab.dataset.productTab === tabId);

      if (!activeTab) {
        return;
      }

      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.setAttribute('aria-selected', isActive.toString());
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.productPanel !== tabId;
      });

      if (moveFocus) {
        activeTab.focus();
      }

      this.bindActiveGrid();
    }

    handleKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % this.tabs.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.activateTab(this.tabs[nextIndex].dataset.productTab, true);
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId || event.target?.dataset?.blockId;
      const tab = this.tabs.find((item) => item.dataset.blockId === blockId);

      if (tab) {
        this.activateTab(tab.dataset.productTab, true);
      }
    }

    // Draggable scroll-track indicator for the horizontal card scroller,
    // matching the pattern used by cart-popular-add-ons.js. Opt-in: only
    // runs when the markup (.tabbed-product-collections__scroll-indicator)
    // is present, so the homepage section (which doesn't include it) is
    // unaffected. Rebinds to whichever panel's grid is currently visible,
    // since panels are swapped by tab rather than being separate instances.
    initialiseScrollIndicator() {
      if (!this.scrollIndicator || !this.scrollThumb) return;

      this.scrollController = new AbortController();
      const { signal } = this.scrollController;
      const scheduleUpdate = () => {
        if (this.scrollFrame) return;

        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateScrollIndicator();
        });
      };
      this.scheduleScrollUpdate = scheduleUpdate;

      const onPointerDown = (event) => {
        if (!this.activeGrid) return;

        const indicatorRect = this.scrollIndicator.getBoundingClientRect();
        const thumbRect = this.scrollThumb.getBoundingClientRect();
        const maximumScroll = Math.max(0, this.activeGrid.scrollWidth - this.activeGrid.clientWidth);
        const maximumTravel = Math.max(0, indicatorRect.width - thumbRect.width);

        if (maximumScroll <= 0 || maximumTravel <= 0) return;

        event.preventDefault();

        if (event.clientX >= thumbRect.left && event.clientX <= thumbRect.right) {
          this.scrollDragging = true;
          this.scrollDragStartX = event.clientX;
          this.scrollDragStartLeft = this.activeGrid.scrollLeft;
          this.scrollDragMaximumScroll = maximumScroll;
          this.scrollDragMaximumTravel = maximumTravel;
          this.activeGrid.classList.add('is-scrollbar-dragging');
          this.scrollIndicator.classList.add('is-dragging');
          this.scrollIndicator.setPointerCapture?.(event.pointerId);
          return;
        }

        const clickPosition = event.clientX - indicatorRect.left - thumbRect.width / 2;
        const progress = Math.min(1, Math.max(0, clickPosition / maximumTravel));
        this.activeGrid.scrollTo({ left: progress * maximumScroll, behavior: 'smooth' });
      };
      const onPointerMove = (event) => {
        if (!this.scrollDragging || !this.activeGrid) return;

        event.preventDefault();
        const delta = event.clientX - this.scrollDragStartX;
        const scrollLeft =
          this.scrollDragStartLeft + delta * (this.scrollDragMaximumScroll / this.scrollDragMaximumTravel);
        this.activeGrid.scrollLeft = Math.min(this.scrollDragMaximumScroll, Math.max(0, scrollLeft));
      };
      const endPointerDrag = (event) => {
        if (!this.scrollDragging) return;

        this.scrollDragging = false;
        this.activeGrid?.classList.remove('is-scrollbar-dragging');
        this.scrollIndicator.classList.remove('is-dragging');
        if (this.scrollIndicator.hasPointerCapture?.(event.pointerId)) {
          this.scrollIndicator.releasePointerCapture(event.pointerId);
        }
      };
      const onKeyDown = (event) => {
        if (!this.activeGrid) return;

        const maximumScroll = Math.max(0, this.activeGrid.scrollWidth - this.activeGrid.clientWidth);
        if (maximumScroll <= 0) return;

        let nextScrollLeft = null;
        if (event.key === 'ArrowLeft') {
          nextScrollLeft = this.activeGrid.scrollLeft - this.activeGrid.clientWidth * 0.8;
        }
        if (event.key === 'ArrowRight') {
          nextScrollLeft = this.activeGrid.scrollLeft + this.activeGrid.clientWidth * 0.8;
        }
        if (event.key === 'Home') nextScrollLeft = 0;
        if (event.key === 'End') nextScrollLeft = maximumScroll;

        if (nextScrollLeft === null) return;

        event.preventDefault();
        this.activeGrid.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
      };

      this.scrollDragging = false;
      this.scrollDragStartX = 0;
      this.scrollDragStartLeft = 0;
      this.scrollDragMaximumScroll = 0;
      this.scrollDragMaximumTravel = 0;

      window.addEventListener('resize', scheduleUpdate, { passive: true, signal });
      this.scrollIndicator.addEventListener('pointerdown', onPointerDown, { signal });
      window.addEventListener('pointermove', onPointerMove, { passive: false, signal });
      window.addEventListener('pointerup', endPointerDrag, { signal });
      window.addEventListener('pointercancel', endPointerDrag, { signal });
      this.scrollIndicator.addEventListener('keydown', onKeyDown, { signal });

      if ('ResizeObserver' in window) {
        this.scrollObserver = new ResizeObserver(scheduleUpdate);
        this.scrollObserver.observe(this.scrollIndicator);
      }

      this.bindActiveGrid();
    }

    bindActiveGrid() {
      if (!this.scrollIndicator || !this.scrollThumb) return;

      const nextGrid =
        this.querySelector('[data-product-panel]:not([hidden]) .tabbed-product-collections__grid') ||
        this.querySelector('.tabbed-product-collections__grid');

      if (nextGrid === this.activeGrid) {
        this.scheduleScrollUpdate?.();
        return;
      }

      this.gridScrollController?.abort();
      this.activeGrid = nextGrid;

      if (!this.activeGrid) {
        this.scrollIndicator.hidden = true;
        return;
      }

      this.gridScrollController = new AbortController();
      this.activeGrid.addEventListener('scroll', () => this.scheduleScrollUpdate?.(), {
        passive: true,
        signal: this.gridScrollController.signal,
      });
      this.scrollObserver?.observe(this.activeGrid);
      this.scheduleScrollUpdate?.();
    }

    updateScrollIndicator() {
      if (!this.activeGrid) return;

      const scrollWidth = this.activeGrid.scrollWidth;
      const visibleWidth = this.activeGrid.clientWidth;
      const trackWidth = this.scrollIndicator.clientWidth;
      const maximumScroll = Math.max(0, scrollWidth - visibleWidth);
      const visibleRatio = scrollWidth > 0 ? Math.min(1, visibleWidth / scrollWidth) : 1;
      const thumbWidth = Math.min(trackWidth, Math.max(24, trackWidth * visibleRatio));
      const maximumTravel = Math.max(0, trackWidth - thumbWidth);
      const progress =
        maximumScroll > 0 ? Math.min(1, Math.max(0, this.activeGrid.scrollLeft / maximumScroll)) : 0;

      this.scrollThumb.style.width = `${thumbWidth}px`;
      this.scrollThumb.style.transform = `translateX(${maximumTravel * progress}px)`;
      this.scrollIndicator.classList.toggle('is-hidden', maximumScroll <= 1);
      this.scrollIndicator.hidden = maximumScroll <= 1;
      this.scrollIndicator.setAttribute('aria-hidden', String(maximumScroll <= 1));
      this.scrollIndicator.setAttribute('tabindex', maximumScroll > 1 ? '0' : '-1');
      this.scrollIndicator.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
      this.scrollIndicator.setAttribute('aria-valuetext', `${Math.round(progress * 100)}% scrolled`);
    }
  }

  customElements.define('tabbed-product-collections', TabbedProductCollections);
}
